import * as serviceWorkerMessageTypes from '../consts/serviceWorkerMessageTypes';
import {setSwMessage} from '../state/slice/global';
import {dispatch} from '../state/store';
import log from './logger/log';

const SW_ACTIVATION_TIMEOUT = 60 * 1000; // 1 min

const registerServiceWorker = async () => {
    try {
        return await navigator.serviceWorker.getRegistration();
    } catch (e) {
        log.error('serviceWorkerService: Failed to register service worker:', e);
    }

    return null;
};

const waitServiceWorkerActivated = (swReg) => {
    return new Promise((resolve, reject) => {
        if (swReg?.active) {
            resolve(swReg);
            return;
        }

        const serviceWorker = swReg?.installing || swReg?.waiting;

        if (!serviceWorker) {
            reject(new Error('Service worker is not in an installing or waiting state.'));
            return;
        }

        const timeout = setTimeout(() => {
            removeListeners();
            reject(new Error('Service worker activation timed out.'));
        }, SW_ACTIVATION_TIMEOUT);

        const removeListeners = () => {
            clearTimeout(timeout);
            serviceWorker.removeEventListener('statechange', handleStateChange);
            serviceWorker.removeEventListener('error', handleError);
        };

        const handleStateChange = (event) => {
            if (event.target.state === 'activated') {
                removeListeners();
                resolve();
            }
        };

        const handleError = (error) => {
            removeListeners();
            reject(error);
        };

        serviceWorker.addEventListener('statechange', handleStateChange);
        serviceWorker.addEventListener('error', handleError);
    });
};

const postMessage = async (message) => {
    const swReg = await registerServiceWorker();

    if (!swReg) return;

    try {
        const registration = await navigator.serviceWorker.ready;

        registration.active.postMessage(message);
        log.info(`serviceWorkerService: Successfully sent message to ServiceWorker: ${JSON.stringify(message)}`);
    } catch (e) {
        log.error(
            `serviceWorkerService: Failed to send message to ServiceWorker: ${JSON.stringify(message)}`,
            e.message
        );
    }
};

const postMessageUserIsAuthorized = (isAuthorized) =>
    postMessage({type: serviceWorkerMessageTypes.USER_AUTHORIZE, isAuthorized});

const postMessageAppOpened = () => postMessage({type: serviceWorkerMessageTypes.APP_OPENED});

const postMessageRemoveNotificationsAndBadges = () =>
    postMessage({type: serviceWorkerMessageTypes.REMOVE_NOTIFICATIONS_AND_BADGES});

const subscribeToSWMessages = async () => {
    try {
        await registerServiceWorker();

        navigator.serviceWorker.addEventListener('message', (event) => {
            log.debug(`serviceWorkerService: Received message from Service Worker: ${event.data}`);

            try {
                const data = JSON.parse(event.data);

                dispatch(setSwMessage(data));
            } catch (e) {
                log.error(`serviceWorkerService: Failed to parse Service Worker message: ${event.data}`, e.message);
            }
        });

        navigator.serviceWorker.startMessages();
        await postMessageAppOpened();

        log.debug('serviceWorkerService: Successfully subscribed to Service Worker messages');
    } catch (e) {
        log.error('serviceWorkerService: Failed to subscribe to Service Worker messages', e.message);
    }
};

// NOTE: Forces the waiting service worker to skip the 'waiting' phase and become active immediately.
const postMessageSkipWaiting = async (message = {type: serviceWorkerMessageTypes.SKIP_WAITING}) => {
    try {
        const swReg = await registerServiceWorker();

        if (!swReg) return;

        if (swReg.waiting) {
            const serviceWorker = swReg.waiting;

            // Listen for the statechange event
            const onStateChange = (event) => {
                if (event.target.state === 'activated') {
                    serviceWorker.removeEventListener('statechange', onStateChange);
                    log.info('serviceWorkerService: new SW activated, reloading...');
                    window.location.reload();
                }
            };

            serviceWorker.addEventListener('statechange', onStateChange);

            serviceWorker.postMessage(message);
            log.info(`serviceWorkerService: Sent skipWaiting message to waiting SW`);
        } else {
            log.info('serviceWorkerService: No waiting Service Worker to skip.');
        }
    } catch (e) {
        log.error('serviceWorkerService: Failed to skip waiting.', e.message);
    }
};

export default {
    registerServiceWorker,
    waitServiceWorkerActivated,
    postMessage,
    postMessageUserIsAuthorized,
    postMessageAppOpened,
    postMessageRemoveNotificationsAndBadges,
    postMessageSkipWaiting,
    subscribeToSWMessages,
};
